import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Tracking Pixel IDs from zarooriretail.com
const TRACKING_IDS = {
  // Google Analytics IDs
  GA_ID_1: 'G-H4LH81HG54',
  GA_ID_2: 'G-2KMRRGWGRB',
  // Google Tag Manager IDs
  GTM_ID_1: 'GTM-MLJ2M7G5',
  GTM_ID_2: 'GTM-T2XKDJGZ',
  // Google Ads ID
  GADS_ID: 'AW-16605373833',
  // Meta (Facebook) Pixel IDs
  FB_PIXEL_1: '2152514138511016',
  FB_PIXEL_2: '1253749822663614'
};

const TrackingPixels = () => {
  const location = useLocation();
  const trackingInitialized = useRef(false);

  // Initialize tracking scripts only after user interaction or timeout (LCP optimization)
  useEffect(() => {
    if (trackingInitialized.current) return;

    const initializeTracking = () => {
      if (trackingInitialized.current) return;
      trackingInitialized.current = true;

      // Initialize Google Analytics
      const initGA = () => {
        if (!window.gtag) {
          const script1 = document.createElement('script');
          script1.async = true;
          script1.src = `https://www.googletagmanager.com/gtag/js?id=${TRACKING_IDS.GA_ID_1}`;
          document.head.appendChild(script1);

          window.dataLayer = window.dataLayer || [];
          window.gtag = function() {
            window.dataLayer.push(arguments);
          };
          window.gtag('js', new Date());
          window.gtag('config', TRACKING_IDS.GA_ID_1);
          window.gtag('config', TRACKING_IDS.GA_ID_2);
          window.gtag('config', TRACKING_IDS.GADS_ID);
        }
      };

      // Initialize Google Tag Manager
      const initGTM = () => {
        if (!window.google_tag_manager) {
          (function(w,d,s,l,i){
            w[l]=w[l]||[];
            w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
            var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),
                dl=l!=='dataLayer'?'&l='+l:'';
            j.async=true;
            j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
            f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer', TRACKING_IDS.GTM_ID_1);

          // GTM 2 - Load with slight delay to not block
          setTimeout(() => {
            (function(w,d,s,l,i){
              w[l]=w[l]||[];
              w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),
                  dl=l!=='dataLayer'?'&l='+l:'';
              j.async=true;
              j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', TRACKING_IDS.GTM_ID_2);
          }, 500);
        }
      };

      // Initialize Meta (Facebook) Pixel
      const initFBPixel = () => {
        if (!window.fbq) {
          (function(f,b,e,v,n,t,s){
            if(f.fbq)return;
            n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;
            n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s);
          })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
          
          // Initialize both pixels
          window.fbq('init', TRACKING_IDS.FB_PIXEL_1);
          window.fbq('init', TRACKING_IDS.FB_PIXEL_2);
          window.fbq('track', 'PageView');
        }
      };

      // Stagger the initialization to reduce main thread blocking
      setTimeout(initGA, 100);
      setTimeout(initGTM, 200);
      setTimeout(initFBPixel, 1000);
    };

    // Defer tracking until user interaction or 4 seconds (for LCP optimization)
    const events = ['scroll', 'click', 'touchstart', 'keydown', 'mousemove'];
    const cleanupFns = events.map(event => {
      const handler = () => initializeTracking();
      window.addEventListener(event, handler, { once: true, passive: true });
      return () => window.removeEventListener(event, handler);
    });

    // Fallback: Initialize after 4 seconds if no interaction
    const timeoutId = setTimeout(initializeTracking, 4000);

    return () => {
      cleanupFns.forEach(cleanup => cleanup());
      clearTimeout(timeoutId);
    };
  }, []);

  // Track page views on route change
  useEffect(() => {
    // Google Analytics page view
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }

    // Facebook Pixel page view
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  // GTM noscript fallback - render in body
  return (
    <>
      {/* GTM noscript fallbacks */}
      <noscript>
        <iframe 
          src={`https://www.googletagmanager.com/ns.html?id=${TRACKING_IDS.GTM_ID_1}`}
          height="0" 
          width="0" 
          style={{ display: 'none', visibility: 'hidden' }}
          title="GTM 1"
        />
      </noscript>
      <noscript>
        <iframe 
          src={`https://www.googletagmanager.com/ns.html?id=${TRACKING_IDS.GTM_ID_2}`}
          height="0" 
          width="0" 
          style={{ display: 'none', visibility: 'hidden' }}
          title="GTM 2"
        />
      </noscript>
    </>
  );
};

// Export tracking IDs for use in other components
export { TRACKING_IDS };
export default TrackingPixels;
